import FmdGoodIcon from "@mui/icons-material/FmdGood";
import TripOriginIcon from "@mui/icons-material/TripOrigin";
import { Box, Divider, Grid, Stack, Typography } from "@mui/material";
import { Link } from "react-router-dom";

import { CompanyBusinessTypeChip } from "components/CompanyBusinessTypeChip";
import { ShipmentTypeChip } from "components/ShipmentTypeChip";
import { ShipmentEquipmentTypeText } from "components/Texts/ShipmentEquipmentTypeText";
import { ShipmentFootageText } from "components/Texts/ShipmentFootageText";
import { ShipmentIDText } from "components/Texts/ShipmentIDText";
import { ShipmentLocationText } from "components/Texts/ShipmentLocationText";
import { ShipmentPriceText } from "components/Texts/ShipmentPriceText";
import { ShipmentWeightText } from "components/Texts/ShipmentWeightText";
import { useSubscriptionStatus } from "hooks";
import {
  carrierHasBookingWriteAccess,
  convertSubdivisionCodeToSimplifiedSubdivisionCode,
  formatText,
  getCommdityNameForCondensedShipmentShipment,
  getServiceTypeDisplayLabel,
  ISOStringToShorthandDate,
  shouldShowShipperCompanyName,
} from "utils";

import { EVOTRUX_BLUE, EVOTRUX_GREEN } from "../../../../constants";
import { CompanyNameUpgradeText } from "../CompanyNameUpgradeText";
import { MultiShipmentProjectLoadsAvailableLoadsLabel } from "../MultiShipmentProjectLoadsAvailableLoadsLabel";
import {
  QuotedShipmentChip,
  QuotedShipmentTeammateChip,
} from "../QuotedShipmentChip";

interface Props {
  shipment: CarrierCondensedShipment;
}

export function ShipmentPreview(props: Props) {
  const { shipment } = props;
  const { isPayingCarrier } = useSubscriptionStatus();
  const showCompanyName = shouldShowShipperCompanyName({
    isSubscribed: Boolean(isPayingCarrier),
    isPartnerShipment: shipment.is_partner_shipment,
    isBookedByCarrier: carrierHasBookingWriteAccess(shipment),
  });
  const shipmentInfo = shipment.info;
  const firstPointLocation = shipment?.first_point?.point_location;
  const lastPointLocation = shipment?.last_point?.point_location;

  return (
    <Box
      component={Link}
      to={`/shipments/${shipmentInfo.id}`}
      display="flex"
      px="2rem"
      py="1rem"
      sx={{
        textDecoration: "none",
        color: "inherit",
        "&:hover": {
          bgcolor: "#f8f8f8",
          cursor: "pointer",
        },
        ...(shipment?.is_partner_shipment && {
          borderLeft: `4px solid ${EVOTRUX_GREEN}`,
        }),
      }}
    >
      <Grid
        container
        direction="row"
        justifyContent="flex-start"
        alignItems="center"
      >
        <Grid item xs={4} sm={4} md={4} lg={4} xl={4}>
          <Stack spacing={1}>
            <ShipmentEquipmentTypeText data={shipment?.equipments} />
            <MultiShipmentProjectLoadsAvailableLoadsLabel shipment={shipment} />
            <Typography sx={{ textTransform: "capitalize" }}>
              {getServiceTypeDisplayLabel(shipmentInfo.service)}
            </Typography>
            <ShipmentWeightText data={shipment?.info?.weight_lb} />
            <Stack direction="row" sx={{ flexWrap: "wrap", gap: 1 }}>
              <ShipmentIDText shipment={shipment} />
              <QuotedShipmentChip quoted={shipment?.has_carrier_active_quote} />
              <QuotedShipmentTeammateChip
                teammateQuotes={shipment?.teammate_quotes}
              />
              <ShipmentTypeChip shipmentType={shipment?.info?.type} />
              <CompanyBusinessTypeChip
                businessType={shipment.shipment_company.business_type}
              />
            </Stack>
          </Stack>
        </Grid>
        <Grid item xs={8} sm={8} md={8} lg={8} xl={8}>
          <Box ml="2rem" flex={1} alignSelf="center">
            <Stack direction="row" justifyContent="left">
              {showCompanyName ? (
                <Typography color={EVOTRUX_BLUE}>
                  <b>{shipment?.shipment_company?.name || ""}</b>
                </Typography>
              ) : (
                <CompanyNameUpgradeText />
              )}
            </Stack>
            <Typography sx={{ textTransform: "capitalize" }} variant="body1">
              {formatText(
                getCommdityNameForCondensedShipmentShipment(shipmentInfo),
              )}
            </Typography>
            <Stack my="0.5rem" direction="row" alignItems="center">
              <Stack id="origin" direction="column" spacing={1}>
                <ShipmentLocationText
                  data={`${firstPointLocation.city}, ${firstPointLocation.province ? convertSubdivisionCodeToSimplifiedSubdivisionCode(firstPointLocation.province) : ""}`}
                  icon={
                    <TripOriginIcon
                      sx={{ fontSize: "0.9rem", marginRight: "5px" }}
                    />
                  }
                />

                <Typography variant="body1">
                  Ready{" "}
                  {ISOStringToShorthandDate(shipment?.first_point?.start_time)}
                </Typography>
              </Stack>

              <Box flex={1} mx="1rem">
                <Divider />
              </Box>
              <Stack id="destination" direction="column" spacing={1}>
                <ShipmentLocationText
                  data={`${lastPointLocation.city}, ${lastPointLocation.province ? convertSubdivisionCodeToSimplifiedSubdivisionCode(lastPointLocation.province) : ""}`}
                  icon={
                    <FmdGoodIcon
                      sx={{ fontSize: "0.9rem", marginRight: "5px" }}
                    />
                  }
                />
                <Typography variant="body1">
                  {shipment?.last_point.schedule_type === "DATETIME"
                    ? ISOStringToShorthandDate(shipment?.last_point?.start_time)
                    : "Flexible"}
                </Typography>
              </Stack>
            </Stack>
          </Box>
          <Stack
            direction="row"
            spacing={4}
            mx="2rem"
            justifySelf="flex-end"
            alignSelf="center"
          >
            <ShipmentFootageText data={shipment?.info?.footage} />
            <ShipmentPriceText
              price={shipment?.info?.preferred_price}
              currency={shipment?.info?.currency}
            />
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
}
